// tokenOperations.ts
import type { PlayerTokens } from "./form-calculations.ts";

export type ValidationError = {
  type: "INVALID_PLAYER" | "NEGATIVE_TOKENS" | "INVALID_INPUT";
  message: string;
  playerId?: string;
  attemptedValue?: number;
};

export type TokenUpdateResult = {
  success: boolean;
  updatedPlayers?: PlayerTokens[];
  actualTokensSet?: number;
  error?: ValidationError;
};

/**
 * Validates that a token update is allowed
 */
export const validateTokenUpdate = (
  playerId: string,
  newTokens: number,
  playerTokens: PlayerTokens[],
): ValidationError | null => {
  // Check if playerId is valid
  if (typeof playerId !== "string" || playerId.trim().length === 0) {
    return {
      type: "INVALID_INPUT",
      message: "Player ID must be a non-empty string",
      playerId,
      attemptedValue: newTokens,
    };
  }

  // Check if tokens is a valid amount
  if (
    typeof newTokens !== "number" ||
    !Number.isInteger(newTokens) ||
    newTokens < 0 ||
    !Number.isFinite(newTokens)
  ) {
    return {
      type: "INVALID_INPUT",
      message: "Tokens must be a non-negative integer",
      playerId,
      attemptedValue: newTokens,
    };
  }

  // Check if player exists
  const playerExists = playerTokens.some((p) => p.id === playerId);
  if (!playerExists) {
    return {
      type: "INVALID_PLAYER",
      message: `Player with ID "${playerId}" not found`,
      playerId,
      attemptedValue: newTokens,
    };
  }

  return null; // No validation errors
};

/**
 * Calculates the actual tokens that should be set based on constraints
 */
export const calculateActualTokensToSet = (
  playerId: string,
  requestedTokens: number,
  playerTokens: PlayerTokens[],
  maxTokens: number,
  maxTokensPerPlayer: number,
): number => {
  const player = playerTokens.find((p) => p.id === playerId);
  if (!player) {
    return 0;
  }

  const currentTotal = playerTokens.reduce((sum, p) => sum + p.tokens, 0);
  const currentPlayerTokens = player.tokens;

  // Calculate max tokens this player can have
  const availableTokens = maxTokens - currentTotal + currentPlayerTokens;
  const maxAllowedForPlayer = Math.max(
    0,
    Math.min(availableTokens, maxTokensPerPlayer),
  );

  // If reducing tokens, allow it (but not below 0)
  if (requestedTokens <= currentPlayerTokens) {
    return Math.max(0, requestedTokens);
  }

  // If increasing tokens, cap at maximum allowed
  return Math.min(requestedTokens, maxAllowedForPlayer);
};

/**
 * Creates a new player tokens array with updated tokens for a specific player
 */
export const updatePlayerTokens = (
  playerId: string,
  newTokens: number,
  playerTokens: PlayerTokens[],
): PlayerTokens[] => {
  return playerTokens.map((player) =>
    player.id === playerId ? { ...player, tokens: newTokens } : player,
  );
};

/**
 * Attempts to set tokens for a player with full validation and constraint handling
 */
export const trySetTokensForPlayer = (
  playerId: string,
  requestedTokens: number,
  playerTokens: PlayerTokens[],
  maxTokens: number,
  maxTokensPerPlayer: number,
): TokenUpdateResult => {
  // Validate the input
  const validationError = validateTokenUpdate(
    playerId,
    requestedTokens,
    playerTokens,
  );

  if (validationError) {
    return {
      success: false,
      error: validationError,
    };
  }

  // Calculate actual tokens to set (applying constraints)
  const actualTokens = calculateActualTokensToSet(
    playerId,
    requestedTokens,
    playerTokens,
    maxTokens,
    maxTokensPerPlayer,
  );

  // Create updated player tokens array
  const updatedPlayers = updatePlayerTokens(
    playerId,
    actualTokens,
    playerTokens,
  );

  return {
    success: true,
    updatedPlayers,
    actualTokensSet: actualTokens,
  };
};

/**
 * Applies a random token distribution to players
 */
export const applyRandomDistribution = (
  playerTokens: PlayerTokens[],
  distribution: number[],
): TokenUpdateResult => {
  if (playerTokens.length !== distribution.length) {
    return {
      success: false,
      error: {
        type: "INVALID_INPUT",
        message: "Distribution array length must match number of players",
      },
    };
  }

  // Validate all distribution values
  for (let i = 0; i < distribution.length; i++) {
    const tokens = distribution[i];
    if (
      typeof tokens !== "number" ||
      !Number.isInteger(tokens) ||
      tokens < 0 ||
      !Number.isFinite(tokens)
    ) {
      return {
        success: false,
        error: {
          type: "INVALID_INPUT",
          message: `Invalid token amount at index ${i}: ${tokens}`,
          attemptedValue: tokens,
        },
      };
    }
  }

  // Apply the distribution
  const updatedPlayers = playerTokens.map((player, index) => ({
    ...player,
    tokens: distribution[index],
  }));

  return {
    success: true,
    updatedPlayers,
  };
};

/**
 * Batch update multiple players' tokens at once
 */
export const batchUpdateTokens = (
  updates: Array<{ playerId: string; tokens: number }>,
  playerTokens: PlayerTokens[],
  maxTokens: number,
  maxTokensPerPlayer: number,
): TokenUpdateResult => {
  let currentPlayers = [...playerTokens];
  const errors: ValidationError[] = [];

  // Apply updates one by one
  for (const update of updates) {
    const result = trySetTokensForPlayer(
      update.playerId,
      update.tokens,
      currentPlayers,
      maxTokens,
      maxTokensPerPlayer,
    );

    if (!result.success && result.error) {
      errors.push(result.error);
    } else if (result.updatedPlayers) {
      currentPlayers = result.updatedPlayers;
    }
  }

  if (errors.length > 0) {
    return {
      success: false,
      error: {
        type: "INVALID_INPUT",
        message: `Batch update failed with ${errors.length} errors`,
      },
    };
  }

  return {
    success: true,
    updatedPlayers: currentPlayers,
  };
};

/**
 * Validates that a complete token distribution is valid
 */
export const validateDistribution = (
  playerTokens: PlayerTokens[],
  maxTokens: number,
  maxTokensPerPlayer: number,
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Check individual player limits
  playerTokens.forEach((player) => {
    if (player.tokens > maxTokensPerPlayer) {
      errors.push(
        `Player ${player.id} has ${player.tokens} tokens, exceeding limit of ${maxTokensPerPlayer}`,
      );
    }

    if (player.tokens < 0) {
      errors.push(`Player ${player.id} has negative tokens: ${player.tokens}`);
    }
  });

  // Check total limit
  const totalTokens = playerTokens.reduce((sum, p) => sum + p.tokens, 0);

  if (totalTokens > maxTokens) {
    errors.push(`Total tokens ${totalTokens} exceeds maximum of ${maxTokens}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
